// Student API service
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.afriqcm.com';
  }
  // In development, use proxy (no origin needed)
  return '';
};

const API_BASE_URL = getApiBaseUrl();
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
const STUDENT_API_URL = API_BASE_URL ? `${API_BASE_URL}/${API_VERSION}/students` : `/${API_VERSION}/students`;
const CENTERS_URL = API_BASE_URL ? `${API_BASE_URL}/${API_VERSION}/centers/` : `/${API_VERSION}/centers/`;
const REGIONS_URL = API_BASE_URL ? `${API_BASE_URL}/${API_VERSION}/regions/` : `/${API_VERSION}/regions/`;

const handleApiResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Invalid response format: ${text}`);
  }

  const data = await response.json();
  
  if (data.status === 'success') {
    return data;
  } else {
    throw new Error(data.message || 'API error');
  }
};

const handleApiError = async (response) => {
  const contentType = response.headers.get('content-type');
  let errorData;
  
  try {
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    } else {
      const text = await response.text();
      errorData = {
        status: 'error',
        message: text || `HTTP ${response.status} Error`,
        error: `HTTP ${response.status}`,
        details: []
      };
    }
  } catch (e) {
    errorData = {
      status: 'error',
      message: `HTTP ${response.status} Error`,
      error: `HTTP ${response.status}`,
      details: []
    };
  }

  // Extract field-specific errors from details array
  const fieldErrors = {};
  if (errorData.details && Array.isArray(errorData.details)) {
    errorData.details.forEach(detail => {
      if (detail.field) {
        fieldErrors[detail.field] = detail.message || detail.code || 'Invalid value';
      }
    });
  }

  return {
    message: errorData.message || errorData.error || `HTTP ${response.status} Error`,
    fieldErrors: fieldErrors,
    statusCode: response.status,
    errorData: errorData
  };
};

/**
 * Fetch centers for the student application (Center of Choice dropdown).
 * GET /v1/centers/
 * @returns {Promise<Array<{ id, name, full_name, region_id, region, amounts, phase_config, created_at }>>}
 */
export const getCenters = async () => {
  const response = await fetch(CENTERS_URL, {
    method: 'GET',
    headers: { accept: 'application/json' },
    mode: 'cors',
  });
  if (!response.ok) {
    throw new Error('Failed to load centers');
  }
  const json = await response.json();
  if (json.status !== 'success' || !Array.isArray(json.data)) {
    throw new Error(json.message || 'Invalid centers response');
  }
  return json.data;
};

/**
 * Fetch regions for the student application (Region dropdown).
 * GET /v1/regions/
 * @returns {Promise<Array<{ id, name, code, created_at }>>}
 */
export const getRegions = async () => {
  const response = await fetch(REGIONS_URL, {
    method: 'GET',
    headers: { accept: 'application/json' },
    mode: 'cors',
  });
  if (!response.ok) {
    throw new Error('Failed to load regions');
  }
  const json = await response.json();
  if (json.status !== 'success' || !Array.isArray(json.data)) {
    throw new Error(json.message || 'Invalid regions response');
  }
  return json.data;
};

export const submitStudentApplication = async (applicationData) => {
  try {
    const url = `${STUDENT_API_URL}/apply`;
    console.log('Submitting student application to:', url);
    console.log('Application data:', applicationData);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(applicationData),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    // Handle success (201)
    if (response.status === 201) {
      const data = await handleApiResponse(response);
      console.log('Student application submitted successfully:', data);
      return {
        success: true,
        data: data.data,
        message: data.message || 'Application submitted successfully'
      };
    }

    // Handle errors (400, 404, 422, 500)
    const error = await handleApiError(response);
    console.error('Student application error:', error);
    
    return {
      success: false,
      message: error.message,
      fieldErrors: error.fieldErrors,
      statusCode: error.statusCode,
      errorData: error.errorData
    };

  } catch (error) {
    console.error('Student application fetch error:', error);
    
    // Handle network errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return {
        success: false,
        message: 'Network error: Unable to connect to the server. Please check your internet connection and try again.',
        fieldErrors: {},
        statusCode: 0
      };
    }

    return {
      success: false,
      message: error.message || 'An unexpected error occurred. Please try again later.',
      fieldErrors: {},
      statusCode: 0
    };
  }
};

